/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  Component,
  InputHTMLAttributes,
  RefObject,
  ReactNode,
} from 'react'

import classNames from 'classnames'

import styles from './Input.module.scss'

export interface IProps extends InputHTMLAttributes<HTMLInputElement> {
  dark?: boolean
  type?: string
  required?: boolean
  rounded?: boolean
  childRef?: RefObject<HTMLInputElement>
  postfix?: JSX.Element
  icon?: string
  error?: boolean
  success?: boolean
  desc?: string
  label?: string
  inputClassName?: string
  containerClasses?: string
  onClear?: () => void
  children?: ReactNode
  mask?: string
}

type State = {
  visibleClear: boolean
}

export class SimpleInput extends Component<IProps, State> {
  static defaultProps: IProps = {
    rounded: true,
  }

  ref = React.createRef<HTMLInputElement>()

  constructor(public props: IProps) {
    super(props)

    this.state = {
      visibleClear: !!props?.value || false,
    }
  }

  componentDidUpdate(): void {
    this.handleChange()
  }

  handleChange = (e?: React.ChangeEvent<HTMLInputElement>): void => {
    const ref = this.props.childRef || this.ref
    const v = Boolean(ref?.current?.value)
    if (v !== this.state.visibleClear) {
      this.setState({ visibleClear: v })
    }
    if (e && this.props.onChange) {
      this.props.onChange(e)
    }
  }

  renderWrapper = (): JSX.Element => {
    const {
      dark,
      icon,
      required,
      className = '',
      rounded,
      postfix,
      error,
      success,
      label,
      disabled,
      content,
    } = this.props

    const wrapperClass = classNames([
      styles.wrapper,
      className,
      {
        [styles.dark]: dark,
        [styles.label]: label,
        [styles.required]: required,
        [styles.disabled]: disabled,
        [styles.error]: error,
        [styles.success]: success,
        [styles.rounded]: rounded,
        [styles.withPostfix]: postfix || this.state.visibleClear,
        [styles.withPrefix]: icon,
      },
    ])

    const input = this.renderInput()

    return label ? (
      <label className={wrapperClass}>
        <small>{label}</small>
        {input}
      </label>
    ) : (
      <div className={wrapperClass}>{input}</div>
    )
  }

  renderIcon = (isError?: boolean): JSX.Element => (
    <span className={styles.icon}>
      <i className={isError ? 'icon-close' : 'icon-check'} />
    </span>
  )

  renderPostfix = (): JSX.Element | null => {
    const { onClear, postfix } = this.props

    if (this.state.visibleClear) {
      return (
        <i
          className={`${styles.clear} icon-close`}
          onClick={() => onClear && onClear()}
        />
      )
    }

    return postfix ? postfix : null
  }

  renderInput = (): JSX.Element => {
    const {
      type,
      icon,
      error,
      childRef = this.ref,
      success,
      desc,
      inputClassName = '',
      containerClasses = '',
      rounded,
      onClear,
      content,
      dark,
      ...rest
    } = this.props

    return (
      <>
        <div className={`${styles.input__wrapper} ${containerClasses}`}>
          {content}
          {icon && <i className={`${icon} ${styles.prefix}`} />}
          <input
            {...rest}
            type={type}
            ref={childRef}
            className={inputClassName}
            onChange={this.handleChange}
          />
          {(error || success) && this.renderIcon(error)}
          {this.renderPostfix()}
        </div>
        {desc && <span className={styles.desc}>{desc}</span>}
      </>
    )
  }

  render(): JSX.Element {
    return this.renderWrapper()
  }
}
